"use client";

import Link from "next/link";

const TermsOfService = () => {
  return (
    <main className="py-12 md:py-16">
      <div className="w-[90%] max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Terms of Service
          </h1>
          <p className="mt-4 text-sm opacity-70">
            Effective Date: January 2025 | Last Updated: January 2025
          </p>
        </div>

        {/* Main Content Card */}
        <div className="rounded-[18px] bg-gray-100 dark:bg-white/5 backdrop-blur-sm border border-gray-200 dark:border-white/10 p-6 md:p-10">
          {/* Intro */}
          <p className="leading-relaxed opacity-90">
            These Terms of Service (&quot;Terms&quot;) govern your access to and
            use of Lattu.ai.
          </p>

          {/* Section 1 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              1. Service Description
            </h2>
            <p className="mt-3 opacity-90">
              Lattu.ai provides informational and AI-assisted planning tools
              related to international education, travel, employment, and
              migration. The platform offers structured workflows, cost
              estimates, timelines, document guidance, and comparative insights.
            </p>
            <div className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Important:</strong> Lattu.ai is not a government
                authority, immigration law firm, educational institution, or
                licensed consultancy.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              2. No Legal or Official Guarantee
            </h2>
            <p className="mt-3 opacity-90">
              All content is provided for informational and planning purposes
              only. Final decisions, approvals, and outcomes depend on relevant
              government agencies, universities, employers, and legal
              authorities. Users are responsible for verifying information with
              official sources.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              3. AI-Generated Content
            </h2>
            <p className="mt-3 opacity-90">
              Certain content is generated or structured using artificial
              intelligence. While we strive for accuracy and currency,
              regulations and requirements may change. Lattu.ai does not
              guarantee completeness or legal validity.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              4. User Obligations
            </h2>
            <p className="mt-3 opacity-90">You agree to:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside opacity-80">
              <li>Provide accurate and lawful information</li>
              <li>
                Use the platform for legitimate personal planning purposes
              </li>
              <li>
                Not misuse, copy, scrape, reverse engineer, or interfere with
                platform systems
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              5. Intellectual Property
            </h2>
            <p className="mt-3 opacity-90">
              All platform content, software, workflows, visual design, and
              databases are the intellectual property of Lattu.ai and may not be
              reproduced or distributed without written permission.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              6. Future Services and Paid Features
            </h2>
            <p className="mt-3 opacity-90">
              Lattu.ai may introduce premium tools, expert consultations,
              partner services, or subscription plans. Additional terms may
              apply to such offerings.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              7. Limitation of Liability
            </h2>
            <p className="mt-3 opacity-90">Lattu.ai shall not be liable for:</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Visa or admission refusals",
                "Immigration or travel delays",
                "Financial losses",
                "Missed deadlines",
                "Decisions made based on platform content",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 px-4 py-3"
                >
                  <span className="text-red-500 dark:text-red-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </span>
                  <span className="text-sm opacity-80">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 8 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              8. Account Suspension
            </h2>
            <p className="mt-3 opacity-90">
              We reserve the right to suspend or terminate access for violations
              of these Terms or misuse of the platform.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">9. Modifications</h2>
            <p className="mt-3 opacity-90">
              We may revise these Terms at any time. Continued use of Lattu.ai
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="mt-10 pt-6 border-t border-gray-200 dark:border-white/10">
            <p className="text-sm opacity-70 text-center">
              For questions about these Terms, please contact us through our{" "}
              <Link
                href="/#contact"
                className="text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                Contact Form
              </Link>
              .
            </p>
          </section>
        </div>
      </div>
    </main>
  );
};

export default TermsOfService;
